import React, { useState, useCallback, useEffect } from 'react';

import { Grid, GridColumn, getSelectedState } from '@progress/kendo-react-grid';

import LicenseLinkCell from 'views/user/about/LicenseLinkCell.jsx';
import LicenseNameCell from 'views/user/about/LicenseNameCell.jsx';
import LoadingPanel from 'views/widgets/LoadingPanel.jsx';

export default function ThirdPartyLicensesView() {
	
	const [ frontendLicenseState, setFrontendLicenseState ] = useState({ frontendLicensesFetched: false, frontendLicenses: [] });
	const [ backendLicenseState, setBackendLicenseState ] = useState({ backendLicensesFetched: false, backendLicenses: [] });
	const [ selectedState, setSelectedState ] = useState({});
	
	const DATA_ITEM_KEY = "product";
	const SELECTED_FIELD = "selected";
	
	useEffect(() => {
		
		// fetch front end licenses
        fetch('../../../../licenses/licenses.json')
		.then(response => {
			if(response.ok)
				return response.json();
			else
				throw new Error("Failed to load frontend licenses");
		})
        .then((data) => {
			setFrontendLicenseState({ ...frontendLicenseState, frontendLicensesFetched: true, frontendLicenses: getFrontendLicenses(data) });				
		})
        .catch(() => {
			setFrontendLicenseState({ ...frontendLicenseState, frontendLicensesFetched: true, frontendLicenses: [] });
		});
		
		// fetch back end licenses
		fetch('../../../../licenses/backend-licenses.xml')
		.then(response => {
			if(response.ok)
				return response.text();
			else
				throw new Error("Failed to load backend licenses");
		})
		.then((data) => {
			setBackendLicenseState({ ...backendLicenseState, backendLicensesFetched: true, backendLicenses: getBackendLicenses(data) });
		})
		.catch(() => {
			setBackendLicenseState({ ...backendLicenseState, backendLicensesFetched: true, backendLicenses: [] });
		});
	}, []);
		
	const getFrontendLicenses = (licenses) => {
		let data = [];
		Object.keys(licenses).forEach(pkg => {
			data.push({ product: pkg, licenses: [ {licenseType: licenses[pkg].licenses, licenseFile: licenses[pkg].licenseFile } ] });
		});
		return data;
	};
	
	const getBackendLicenses = (xml) => {
		const parser = new DOMParser();
		const xmlDoc = parser.parseFromString(xml, 'text/xml');
		
		const dependencies = xmlDoc.getElementsByTagName('dependency');
		const parsedData = Array.from(dependencies).map(dependency => ({
			product: dependency.getElementsByTagName('groupId')[0].textContent + ':' 
						+ dependency.getElementsByTagName('artifactId')[0].textContent + ':' 
						+ dependency.getElementsByTagName('version')[0].textContent,
			licenses: Array.from(dependency.getElementsByTagName('license')).map(license => ({
						licenseType: license.getElementsByTagName('name')[0].textContent,
						licenseUrl: license.getElementsByTagName('url')[0].textContent
					}))
		}));
		return parsedData.filter((license) => !license.product.includes('com.constituo')); // exclude constituo libraries
	};
	
	const onSelectionChange = useCallback(
		(event) => {
			const newSelectedState = getSelectedState({
				event,
				selectedState: selectedState,
				dataItemKey: DATA_ITEM_KEY
			});
			setSelectedState(newSelectedState);
		}, [selectedState]
	);
	
	const getColumns = () => {
		return [
			(
				<GridColumn 
					key="product"
					field="product"
					width={200}
					resizable={true}	
					title="Product"
					cells={{ data: LicenseNameCell }}
				/>
			),
			(
				<GridColumn
					key="license"
					field="license"
					width={200}
					resizable={true}
					title="License"
					cells={{ data: LicenseLinkCell }}
				/>
			)
		]
	};
	
	const combineLicenses = () => {
		let licenses = frontendLicenseState.frontendLicenses.concat(backendLicenseState.backendLicenses);
		licenses.sort((a, b) => {
			let fa = a.product.toLowerCase(), fb = b.product.toLowerCase();
			if (fa < fb) return -1;
			if (fa >= fb) return 1;
		});
		return licenses;
		//return backendLicenseState.backendLicenses;
	};
	
	if (!frontendLicenseState.frontendLicensesFetched || !backendLicenseState.backendLicensesFetched)
		return (<div style={{ width: "100%", height: "100%"}}><LoadingPanel useMargin={false}/></div>);
	else
		return (
			<div 
				role="main" 
				aria-label={"Third Party Licenses"}
				style={{ width: "100%" }}
			>
				<Grid
					// grid selection
					selectedField={SELECTED_FIELD}
					selectable={{
						enabled: true,
						drag: false,
						cell: false,
						mode: "single"
					}}
					onSelectionChange={onSelectionChange}
			
					// grid styling								
					style={{ width: "100%" }}
					rowHeight={20}
					resizable={true}
			
					// grid data
					data={combineLicenses()}
					dataItemKey={DATA_ITEM_KEY}
						
					navigatable={true} 
				>
					{getColumns()}
				</Grid>			
			</div>
		);
}
